'use client';

import { AnchorHTMLAttributes, ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';
import { Link } from '@/i18n/routing';
import { cn } from '@ecom/helper-react-class-combine';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'noStyle'
  | 'disabled'
  | 'purchase'
  | 'purchaseToken';
type ButtonSize = 'sm' | 'md' | 'lg' | 'noStyle';

type ButtonOwn = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
};

/**
 * Without `href` → renders a `<button>`. Supports `loading` / `disabled`.
 */
type ButtonAsButton = ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonOwn & {
    href?: never;
    loading?: boolean;
    ref?: ForwardedRef<HTMLButtonElement>;
  };

/**
 * With `href` → renders a localized `<Link>` styled to match the button.
 * `loading` / `disabled` are intentionally not part of this branch (anchors
 * don't have those semantics).
 */
type ButtonAsLink = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> &
  ButtonOwn & {
    href: string;
    prefetch?: boolean;
    ref?: ForwardedRef<HTMLAnchorElement>;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const baseClassesFor = (variant: ButtonVariant): string =>
  variant === 'noStyle'
    ? 'inline-flex items-center justify-center capitalize disabled:opacity-30 transition-colors'
    : 'inline-flex items-center justify-center rounded-[3px] font-body-bold capitalize disabled:opacity-30 transition-colors';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-text font-bold transition-shadow duration-200 ease-out ' +
    'hover:bg-primary-hover ' +
    'hover:shadow-[0_0_5px_rgba(117,198,54,0.45),0_0_10px_rgba(117,198,54,0.25)]',
  secondary: 'bg-transparent hover:bg-secondary-bordered-hover border border-border text-text',
  purchase: 'bg-secondary text-text hover:bg-secondary-hover',
  purchaseToken: 'bg-secondary-contrast text-text hover:bg-secondary-contrast-hover',
  disabled: 'bg-gray-500 text-text hover:bg-gray-600',
  noStyle: 'bg-transparent text-text hover:bg-transparent',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'text-[14px] px-4 py-2',
  md: 'text-[16px] px-6 py-3',
  lg: 'text-[18px] px-8 py-4',
  noStyle: 'text-base px-0 py-0',
};

export const Button = (props: ButtonProps) => {
  const {
    variant = 'primary',
    size = 'md',
    className,
    iconLeft,
    iconRight,
    children,
    ...rest
  } = props;

  const computedClassName = cn(
    'relative',
    baseClassesFor(variant),
    variantClasses[variant],
    sizeClasses[size],
    className,
  );

  const inner = (
    <>
      {iconLeft && <span className='mr-2'>{iconLeft}</span>}
      {children}
      {iconRight && <span className='ml-2'>{iconRight}</span>}
    </>
  );

  if ('href' in rest && rest.href !== undefined) {
    const { href, ref, ...anchorAttrs } = rest;
    return (
      <Link href={href} ref={ref} className={computedClassName} {...anchorAttrs}>
        <span className='inline-flex items-center'>{inner}</span>
      </Link>
    );
  }

  const { ref, disabled, loading = false, ...buttonAttrs } = rest;
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      className={computedClassName}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...buttonAttrs}
    >
      <span className={cn('inline-flex items-center', loading && 'invisible')}>{inner}</span>
      {loading && (
        <span className='absolute inset-0 flex items-center justify-center' aria-hidden='true'>
          <span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
        </span>
      )}
    </button>
  );
};
